import React from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import {
    ALL_TEETH,
    CX,
    getToothDescriptor,
    getToothName,
    LOWER_ROW,
    PAD,
    ROW_HEIGHT,
    UPPER_ROW,
    VB_HEIGHT,
} from './teeth';

export { ALL_TEETH, LOWER_ROW, UPPER_ROW, getToothName };

function Tooth({ fdi, selected, hasRemark, onToggle, showTooltip = true }) {
    const { t } = useTranslation();
    const descriptor = getToothDescriptor(fdi);
    if (!descriptor) return null;
    const { Component, halfWidth, jaw, isMirrored } = descriptor;

    const vbWidth = (halfWidth + PAD) * 2;
    const vbX = CX - halfWidth - PAD;
    const isUpper = jaw === 'upper';

    const numberBadge = (
        <span className={`dental-tooth__number ${selected ? 'is-selected' : ''}`}>
            {fdi}
            {hasRemark && <i className="dental-tooth__dot" />}
        </span>
    );

    const button = (
        <button
            type="button"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onToggle?.(fdi);
            }}
            aria-pressed={selected}
            aria-label={t('dental.tooth', { id: fdi })}
            className={`dental-tooth ${selected ? 'is-selected' : ''} ${isUpper ? '' : 'dental-tooth--lower'}`}
        >
            {isUpper && numberBadge}
            <svg
                width={vbWidth * (ROW_HEIGHT / VB_HEIGHT)}
                height={ROW_HEIGHT}
                viewBox={`${vbX} 0 ${vbWidth} ${VB_HEIGHT}`}
                className="dental-tooth__svg"
                aria-hidden
            >
                <rect x={vbX} y={0} width={vbWidth} height={VB_HEIGHT} fill="transparent" />
                <g transform={isMirrored ? `translate(${CX * 2} 0) scale(-1 1)` : undefined}>
                    <Component jaw={jaw} />
                </g>
            </svg>
            {!isUpper && numberBadge}
        </button>
    );

    if (!showTooltip) return button;

    return (
        <Tooltip title={t('dental.tooth', { id: fdi })} mouseEnterDelay={0.25}>
            {button}
        </Tooltip>
    );
}

export default function DentalChart({
    selected = [],
    remarks = {},
    onToggle,
    upperRow = UPPER_ROW,
    lowerRow = LOWER_ROW,
    showRemarksLegend = true,
    showTooltip = true,
}) {
    const { t } = useTranslation();
    const selectedSet = new Set(selected.map(Number));

    const renderRow = (row, isUpper) => (
        <div className={`dental-chart__row ${isUpper ? '' : 'dental-chart__row--lower'}`}>
            {row.map((fdi, i) => (
                <React.Fragment key={fdi}>
                    {i === Math.floor(row.length / 2) && <span className="dental-chart__midline" aria-hidden />}
                    <Tooth
                        fdi={fdi}
                        selected={selectedSet.has(Number(fdi))}
                        hasRemark={Boolean(remarks[fdi]?.trim?.())}
                        onToggle={onToggle}
                        showTooltip={showTooltip}
                    />
                </React.Fragment>
            ))}
        </div>
    );

    return (
        <div className="dental-chart">
            <div className="dental-chart__scroll">
                <div className="dental-chart__arch">
                    {renderRow(upperRow, true)}
                    {renderRow(lowerRow, false)}
                </div>
            </div>

            <div className="dental-chart__legend">
                <span className="dental-chart__legend-item">
                    <i className="dental-chart__swatch" /> {t('dental.unselected')}
                </span>
                <span className="dental-chart__legend-item">
                    <i className="dental-chart__swatch is-selected" /> {t('dental.selected')}
                </span>
                {showRemarksLegend && (
                <span className="dental-chart__legend-item">
                    <i className="dental-chart__swatch has-remark" /> {t('dental.hasRemark')}
                </span>
                )}
                <span className="dental-chart__legend-count">
                    {t('dental.selectedCount')}: <strong>{selectedSet.size}</strong>
                </span>
            </div>
        </div>
    );
}
